import { lazy, Suspense, useEffect, useState } from "react";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { siteById, SITES, retiredSiteTarget } from "@/data/sites";
import { posterOg } from "@/data/posters";
import { usePoster } from "@/hooks/use-poster";
import { useCollection } from "@/store/collection";
import { canonical, hreflangLinks } from "@/lib/seo";
import { isHeroSite } from "@/data/print-skus";
import { SiteBreadcrumbs, breadcrumbJsonLd } from "@/components/SiteBreadcrumbs";
import { SiteFactChips } from "@/components/SiteFactChips";
import { SiteMiniMap } from "@/components/SiteMiniMap";
import { SiteNowCard } from "@/components/SiteNowCard";
import { SiteAffiliateBlock } from "@/components/SiteAffiliateBlock";
import { SiteBestTime } from "@/components/SiteBestTime";


const SiteDeepDive = lazy(() =>
  import("@/components/SiteDeepDive").then((m) => ({ default: m.SiteDeepDive })),
);
const SiteHistoricalImagery = lazy(() =>
  import("@/components/SiteHistoricalImagery").then((m) => ({
    default: m.SiteHistoricalImagery,
  })),
);
// Airport dataset is sizeable — keep it out of the initial page chunk.
const SiteNearestAirports = lazy(() =>
  import("@/components/SiteNearestAirports").then((m) => ({
    default: m.SiteNearestAirports,
  })),
);

export const Route = createFileRoute("/sites/$siteId")({
  loader: async ({ params }) => {
    const site = siteById(params.siteId);
    if (!site) {
      // Merged duplicates and non-UNESCO entries keep working as permanent redirects.
      const retired = retiredSiteTarget(params.siteId);
      if (retired) throw redirect({ to: "/sites/$siteId", params: { siteId: retired }, statusCode: 301 });
      if (retired === null) throw redirect({ to: "/countries", statusCode: 301 });
      throw notFound();
    }
    return { site };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.site;
    if (!s) return { meta: [{ title: "Site — World Heritage Atlas" }] };
    const baseSuffix = " — World Heritage Atlas";
    const longTitle = `${s.name}, ${s.country}${baseSuffix}`;
    const title = longTitle.length <= 60 ? longTitle : `${s.name}${baseSuffix}`.slice(0, 60);
    const desc = `${s.short} A UNESCO World Heritage Site in ${s.country} (${s.region}), inscribed ${s.year}.`;
    // Direct Supabase CDN URL — social crawlers (Twitter, Slack, WhatsApp,
    // LinkedIn) do not follow 302 redirects for og:image, so we bypass
    // the /og/$siteId route and inline the cropped 1200×630 transform.
    const ogImage = posterOg(s.id) ?? canonical(`/og/${s.id}`);
    const pageUrl = canonical(`/sites/${s.id}`);
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": ["TouristAttraction", "LandmarksOrHistoricalBuildings"],
      "@id": `${pageUrl}#attraction`,
      url: pageUrl,
      name: s.name,
      alternateName: s.officialName ? [s.officialName, `${s.name}, ${s.country}`] : `${s.name}, ${s.country}`,
      description: desc,
      ...(s.unescoId ? { sameAs: `https://whc.unesco.org/en/list/${s.unescoId}/` } : {}),
      image: {
        "@type": "ImageObject",
        url: ogImage,
        width: 1200,
        height: 630,
        caption: `${s.name} — illustrated travel poster`,
      },
      address: { "@type": "PostalAddress", addressCountry: s.country },
      geo: { "@type": "GeoCoordinates", latitude: s.lat, longitude: s.lng },
      containedInPlace: { "@type": "Country", name: s.country },
      isAccessibleForFree: true,
      publicAccess: true,
      touristType: "UNESCO World Heritage",
      additionalType: "https://www.wikidata.org/wiki/Q9259",
      keywords: [
        "UNESCO World Heritage Site",
        s.country,
        s.region,
        s.category,
        ...s.themes,
      ].join(", "),
      additionalProperty: [
        { "@type": "PropertyValue", name: "Year inscribed", value: String(s.year) },
        { "@type": "PropertyValue", name: "UNESCO category", value: s.category },
        { "@type": "PropertyValue", name: "Region", value: s.region },
      ],
      isPartOf: {
        "@type": "WebSite",
        name: "World Heritage Atlas",
        url: canonical("/"),
      },
      hasMap: canonical(`/map?focus=${s.id}`),
      subjectOf: {
        "@type": "WebPage",
        "@id": pageUrl,
        name: title,
        primaryImageOfPage: ogImage,
      },
    };
    const breadcrumbLd = breadcrumbJsonLd(s);

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: pageUrl },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${s.name} — illustrated travel poster` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${s.name} — World Heritage Atlas` },
        { name: "twitter:description", content: s.short },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: pageUrl },
        ...hreflangLinks(`/sites/${s.id}`),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  errorComponent: ({ error }) => <div className="p-20 text-center text-foreground/60">{error instanceof Error ? error.message : String(error)}</div>,
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center text-center">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-foreground/45">Off the map</p>
        <h1 className="font-display mt-4 text-5xl">Site not found</h1>
        <Link to="/explore" className="mt-6 inline-block text-xs uppercase tracking-[0.22em] text-foreground/70 hover:text-foreground">Back to gallery →</Link>
      </div>
    </div>
  ),
  component: SiteDetail,
});

function SiteDetail() {
  const { site } = Route.useLoaderData();
  const src = usePoster(site.id);
  const fav = useCollection((s) => s.has("favorites", site.id));
  const visited = useCollection((s) => s.has("visited", site.id));
  const bucket = useCollection((s) => s.has("bucket", site.id));
  const toggle = useCollection((s) => s.toggle);
  const related = site.related.map(siteById).filter(Boolean) as typeof SITES;
  const printAvailable = isHeroSite(site.id);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [shareLabel, setShareLabel] = useState<string | null>(null);

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const shareUrl = window.location.href;
    const shareData = {
      title: `${site.name} — World Heritage Atlas`,
      text: site.short,
      url: shareUrl,
    };
    const nav = window.navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>;
      canShare?: (data: ShareData) => boolean;
    };
    try {
      if (nav.share && (!nav.canShare || nav.canShare(shareData))) {
        await nav.share(shareData);
        return;
      }
    } catch (err) {
      // User cancelled share sheet — silently ignore.
      if ((err as DOMException)?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel("Link copied");
    } catch {
      setShareLabel("Copy failed");
    }
    window.setTimeout(() => setShareLabel(null), 1800);
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen]);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative min-h-dvh bg-background pb-32 outline-none"
      style={{ ["--site-accent" as never]: site.accent }}
    >
      <section className="relative w-full overflow-hidden pb-10 pt-24 sm:pt-28">
        {/* Ambient wash from the poster's accent — no cropped imagery. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ background: `radial-gradient(80% 60% at 20% 0%, ${site.accent}33, transparent 70%)` }}
        />
        <button
          onClick={() => window.history.back()}
          className="absolute left-6 top-14 z-[60] flex items-center gap-2 rounded-full border border-foreground/25 bg-background/55 px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] text-foreground/90 backdrop-blur-md transition-colors hover:bg-background/80 hover:text-foreground hover:ring-1 hover:ring-[var(--site-accent)] sm:left-10 sm:top-16"
          style={{ boxShadow: `0 8px 30px -8px ${site.accent}44` }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
            <path d="M9 14 4 9l5-5" />
            <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" />
          </svg>
          Back
        </button>
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-6 sm:px-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-14">
          {src && (
            <motion.button
              type="button"
              onClick={() => setLightboxOpen(true)}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              aria-label={`View full poster for ${site.name}`}
              className="group relative mx-auto block w-full max-w-[420px] cursor-zoom-in overflow-hidden rounded-md bg-secondary md:mx-0"
              style={{ boxShadow: `0 40px 90px -40px ${site.accent}88` }}
            >
              <img
                src={src}
                alt={`${site.name} — illustrated travel poster`}
                className="block h-auto w-full transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-background/90 to-transparent pb-3 pt-10 text-[10px] uppercase tracking-[0.22em] text-foreground/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" />
                  <path d="M21 3l-7 7" />
                  <path d="M3 21l7-7" />
                  <path d="M9 21H3v-6" />
                </svg>
                Enlarge poster
              </span>
            </motion.button>
          )}
          <div className="min-w-0">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
              className="text-[11px] uppercase tracking-[0.3em]"
              style={{ color: site.accent }}
            >
              {site.country} · {site.region}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.3 }}
              className="font-display mt-3 text-4xl leading-[0.98] sm:text-6xl md:text-7xl"
            >
              {site.name}
            </motion.h1>
            {site.officialName && (
              <p className="mt-3 max-w-xl text-xs text-foreground/50 text-pretty">
                Official UNESCO name: {site.officialName}
              </p>
            )}
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.55 }}
              className="mt-5 max-w-xl text-base text-foreground/70 text-pretty"
            >
              {site.short}
            </motion.p>
            {src && (
              <motion.button
                onClick={() => setLightboxOpen(true)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="mt-7 inline-flex items-center gap-2 rounded-full border border-foreground/25 bg-background/55 px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] text-foreground/90 backdrop-blur-md transition-colors hover:bg-background/80 hover:text-foreground hover:ring-1 hover:ring-[var(--site-accent)]"
                style={{ boxShadow: `0 8px 30px -8px ${site.accent}44` }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                  <path d="M15 3h6v6" />
                  <path d="M21 3l-7 7" />
                  <path d="M3 21l7-7" />
                  <path d="M9 21H3v-6" />
                </svg>
                View full size
              </motion.button>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-6 max-w-5xl px-6 sm:px-10">
        <SiteBreadcrumbs site={site} />
        <SiteFactChips site={site} />
        <div className="hairline mb-12 mt-8" />
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="text-[10px] uppercase tracking-[0.32em] text-foreground/45">The Story</h2>
            <p className="font-display mt-4 text-2xl leading-snug text-foreground/90 sm:text-3xl text-pretty">
              {site.story}
            </p>
          </div>
          <aside className="space-y-6 text-sm">
            <SiteNowCard site={site} />
            <SiteMiniMap site={site} />
            <Suspense fallback={null}>
              <SiteNearestAirports site={site} />
            </Suspense>
            <Meta label="UNESCO Category" value={site.category} />
            <Meta label="Year Inscribed" value={String(site.year)} />
            <Meta label="Coordinates" value={`${site.lat.toFixed(3)}°, ${site.lng.toFixed(3)}°`} />
            <Meta label="Region" value={site.region} />
            <a
              href={`https://www.google.com/maps?q=${site.lat},${site.lng}`}
              target="_blank" rel="noreferrer"
              className="inline-block text-[10px] uppercase tracking-[0.22em] text-foreground/60 underline-offset-4 hover:text-foreground hover:underline"
              style={{ color: site.accent }}
            >
              Open in maps ↗
            </a>
          </aside>
        </div>

        <div className="mt-16 flex flex-wrap gap-3">
          <Action active={fav} onClick={() => toggle("favorites", site.id)} accent={site.accent}>
            {fav ? "★ Saved" : "☆ Save to favorites"}
          </Action>
          <Action active={visited} onClick={() => toggle("visited", site.id)} accent={site.accent}>
            {visited ? "✓ Visited" : "Mark visited"}
          </Action>
          <Action active={bucket} onClick={() => toggle("bucket", site.id)} accent={site.accent}>
            {bucket ? "● On bucket list" : "+ Bucket list"}
          </Action>
          <Action active={false} onClick={handleShare} accent={site.accent}>
            {shareLabel ?? "↗ Share"}
          </Action>
          <a
            href="#plan-your-visit"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("plan-your-visit")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-opacity hover:opacity-90"
            style={{ background: site.accent, color: "#0b0b0b" }}
          >
            Tickets & tours ↓
          </a>
          {printAvailable && (
            <Link
              to="/sites/$siteId/print"
              params={{ siteId: site.id }}
              className="self-center px-2 text-[10px] uppercase tracking-[0.22em] text-foreground/50 underline-offset-4 hover:text-foreground hover:underline"
            >
              Poster print
            </Link>
          )}
        </div>

        <SiteBestTime site={site} />

        <SiteAffiliateBlock site={site} />

        <Suspense fallback={<div className="mt-16 h-24" aria-hidden />}>
          <SiteDeepDive site={site} />
        </Suspense>

        {(site.country.includes("Norway") || site.country.includes("Sweden")) && (
          <Suspense fallback={null}>
            <SiteHistoricalImagery site={site} />
          </Suspense>
        )}

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="text-[10px] uppercase tracking-[0.32em] text-foreground/45">Continue the journey</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to="/sites/$siteId" params={{ siteId: r.id }} className="group block overflow-hidden rounded-md">
                  <div className="aspect-[2/3] overflow-hidden">
                    <RelatedPoster id={r.id} name={r.name} />
                  </div>
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/50">{r.country}</div>
                    <div className="font-display mt-1 text-lg">{r.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <AnimatePresence>
        {lightboxOpen && src && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightboxOpen(false)}
            className="fixed inset-0 z-[900] grid place-items-center bg-background/95 p-6 backdrop-blur-md sm:p-10"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxOpen(false);
              }}
              aria-label="Close"
              className="absolute right-5 top-5 rounded-full border border-foreground/20 bg-background/40 px-3 py-1.5 text-xs text-foreground/80 backdrop-blur-md transition-colors hover:bg-background/60 hover:text-foreground"
            >
              ✕ Close
            </button>
            <motion.img
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              src={src}
              alt={`${site.name} — illustrated travel poster`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-auto max-w-full cursor-zoom-out rounded-md object-contain shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-foreground/45">{label}</div>
      <div className="font-display mt-1 text-xl">{value}</div>
    </div>
  );
}

function RelatedPoster({ id, name }: { id: string; name: string }) {
  const src = usePoster(id);
  return (
    <img
      src={src}
      alt={`${name} — illustrated travel poster`}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
    />
  );
}

function Action({ active, onClick, accent, children }: { active: boolean; onClick: () => void; accent: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors"
      style={{
        borderColor: active ? accent : "rgba(255,255,255,0.15)",
        color: active ? accent : "rgba(244,240,234,0.7)",
        background: active ? `${accent}14` : "transparent",
      }}
    >
      {children}
    </button>
  );
}